import { client } from '@/api/client'
import { z } from 'zod'
import type { ServerApiResponse } from '../domain/types'
import type {
  NodeConfigPatchDto,
  CreateNodeConfigPatchRequest,
  UpdateNodeConfigPatchRequest,
} from '../domain/patchTypes'
import { components } from '@/api/typeApi'

const ProcessorSchema = z.object({
  id: z.string(),
  socket: z.string().optional(),
  manufacturer: z.string().optional(),
  product_name: z.string().optional(),
  cpu_cores: z.number().optional(),
  thread_count: z.number().optional(),
  max_speed: z.number().optional(),
})

const MemorySchema = z.object({
  id: z.string(),
  size: z.number().optional(),
  manufacturer: z.string().optional(),
  device_locator: z.string().optional(),
})

const DiskSchema = z.object({
  id: z.string(),
  size: z.number().optional(),
  io_size: z.number().optional(),
  pretty_size: z.string().optional(),
  type: z.string().optional(),
  model: z.string().optional(),
  wwid: z.string().optional(),
})

const NetworkInterfaceSchema = z.object({
  id: z.string(),
  link_state: z.boolean().optional(),
  driver: z.string().optional(),
  product: z.string().optional(),
  vendor: z.string().optional(),
  hardware_addr: z.string().optional(),
})

const ServerResourcesSchema = z.object({
  node_addresses: z.array(z.string()).default([]),
  processors: z.array(ProcessorSchema).default([]),
  memory: z.array(MemorySchema).default([]),
  disks: z.array(DiskSchema).default([]),
  interfaces: z.array(NetworkInterfaceSchema).default([]),
})

export type ProcessorInfo = z.infer<typeof ProcessorSchema>
export type MemoryInfo = z.infer<typeof MemorySchema>
export type DiskInfo = z.infer<typeof DiskSchema>
export type NetworkInterface = z.infer<typeof NetworkInterfaceSchema>
export type ParsedServerResources = z.infer<typeof ServerResourcesSchema>

export function parseServerResources(
  resourcesJson: object,
): ParsedServerResources | null {
  try {
    const rawData = resourcesJson
    const result = ServerResourcesSchema.safeParse(rawData)

    if (result.success) {
      return result.data
    } else {
      console.warn(
        'Resources validation failed, using defaults:',
        result.error.format(),
      )
      return {
        node_addresses: [],
        processors: [],
        memory: [],
        disks: [],
        interfaces: [],
      }
    }
  } catch (error) {
    console.error('Failed to parse server resources JSON:', error)
    return null
  }
}

type ExtendedNodeDto = components['schemas']['ExtendedNodeDto']

export interface ServerDetails extends ExtendedNodeDto {
  parsedResources: ParsedServerResources | null
}

export type ServerStatus = 'ready' | 'maintenance' | 'deleted'
export type ServerNodeType = 'unknown' | 'control_plane' | 'worker'

const isValidServerStatus = (status: string): status is ServerStatus => {
  return ['ready', 'maintenance', 'deleted'].includes(status)
}

const isValidServerNodeType = (
  nodeType: string,
): nodeType is ServerNodeType => {
  return ['unknown', 'control_plane', 'worker'].includes(nodeType)
}

export const serversApi = {
  getServers: async (filters?: {
    status?: string
    nodeType?: string
  }): Promise<ServerApiResponse[]> => {
    const validatedFilters: {
      status?: ServerStatus
      nodeType?: ServerNodeType
    } = {}

    if (
      filters?.status &&
      filters.status !== '' &&
      isValidServerStatus(filters.status)
    ) {
      validatedFilters.status = filters.status
    }

    if (
      filters?.nodeType &&
      filters.nodeType !== '' &&
      isValidServerNodeType(filters.nodeType)
    ) {
      validatedFilters.nodeType = filters.nodeType
    }

    const { data } = await client.GET('/v1/nodes', {
      params: {
        query: {
          pagination: { page: 1, per_page: 1000 },
          ...validatedFilters,
        },
      },
    })
    return data?.items || []
  },

  getServerById: async (id: string): Promise<ServerDetails> => {
    const { data } = await client.GET('/v1/nodes/{node_id}', {
      params: { path: { node_id: id } },
    })

    if (!data) {
      throw new Error(`Server ${id} not found`)
    }

    const parsedResources = data.resources
      ? parseServerResources(data.resources)
      : null

    return {
      ...data,
      parsedResources,
    }
  },
}

export const patchesApi = {
  getNodePatches: async (nodeId: string): Promise<NodeConfigPatchDto[]> => {
    const response = await client.GET('/v1/nodes/{node_id}/patches', {
      params: { path: { node_id: nodeId } },
    })
    return response.data || []
  },

  addPatchToNode: async (
    nodeId: string,
    patch: CreateNodeConfigPatchRequest,
  ): Promise<NodeConfigPatchDto> => {
    const response = await client.POST('/v1/nodes/{node_id}/patches', {
      params: { path: { node_id: nodeId } },
      body: patch,
    })

    if (!response.data) {
      throw new Error(`Failed to create patch for node ${nodeId}`)
    }
    return response.data
  },

  updateNodePatch: async (
    nodeId: string,
    patchId: string,
    patch: UpdateNodeConfigPatchRequest,
  ): Promise<NodeConfigPatchDto[]> => {
    const response = await client.PATCH(
      '/v1/nodes/{node_id}/patches/{patch_id}',
      {
        params: { path: { node_id: nodeId, patch_id: patchId } },
        body: patch,
      },
    )
    return response.data || []
  },

  deleteNodePatch: async (nodeId: string, patchId: string): Promise<void> => {
    await client.DELETE('/v1/nodes/{node_id}/patches/{patch_id}', {
      params: { path: { node_id: nodeId, patch_id: patchId } },
    })
  },

  getPatchesSchema: async (): Promise<unknown> => {
    const response = await client.GET('/v1/nodes/patches')
    return response.data
  },
}
